import React, { useCallback, useEffect } from 'react';

import { Icon } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { LoadingAnimation } from 'components/loading-animation';
import { BackArrowLink } from 'components/navigation/back-arrow';
import { useGetUserMembershipsQuery } from 'generated/graphql-gateway';
import { IMemberships } from 'generated/sanity-graphql';
import { useFeatureMemberships } from 'hooks/use-feature-memberships';
import { Account as AccountLayout } from 'pages/account/layout/account';
import { useAuthContext } from 'state/auth';
import { isTestOnly } from 'state/loyalty/utils';
import { routes } from 'utils/routing';

import {
  InfoContainer,
  LinkStatusTag,
  LinkStatusText,
  LoadingContainer,
  MembershipListElement,
} from './styled';

type LinkStatusInfo = {
  partnerName: string;
};

const AccountMemberships = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { featureMemberships, featureMembershipsLoading } = useFeatureMemberships();

  const {
    data,
    loading: membershipStatusLoading,
    refetch: refetchMembershipStatus,
  } = useGetUserMembershipsQuery({
    variables: { userId: user?.cognitoId ?? '' },
    skip: !user?.cognitoId,
  });

  const userMembershipsStatus = data?.getUserMemberships;

  const handleClickMembership = useCallback(
    (membership: IMemberships, linkStatusInfo: LinkStatusInfo | undefined) => {
      const actionButtonLocale = membership?.membershipPageConfiguration?.actionButtonLink as
        | { locale: string }
        | undefined;

      if (!!linkStatusInfo) {
        return navigate(actionButtonLocale?.locale || formatMessage({ id: 'routes.memberships' }), {
          state: { linkStatusInfo },
        });
      }

      return navigate(formatMessage({ id: 'routes.membershipDetails' }), {
        state: { selectedMembership: membership?._id },
      });
    },
    [navigate, formatMessage]
  );

  useEffect(() => {
    refetchMembershipStatus();
  });

  const renderMembershipsList = useCallback(
    () =>
      featureMemberships?.liveMemberships?.map((membership, idx: number) => {
        if (!membership || isTestOnly(membership)) {
          return null;
        }

        const linkStatusInfo = userMembershipsStatus?.find(
          link => link.partnerName === membership?.membershipInternalName
        );
        const hasLinkActive = !!linkStatusInfo;

        return (
          <MembershipListElement
            key={membership?._id || idx}
            onClick={() => handleClickMembership(membership as IMemberships, linkStatusInfo)}
          >
            {membership?.membershipName}

            <InfoContainer>
              {hasLinkActive && (
                <LinkStatusTag>
                  <LinkStatusText>{formatMessage({ id: 'active' })}</LinkStatusText>
                </LinkStatusTag>
              )}

              <Icon
                icon="next"
                color="icon-default"
                width="16px"
                aria-hidden
                data-testid="icon-test-id"
              />
            </InfoContainer>
          </MembershipListElement>
        );
      }),
    [userMembershipsStatus, featureMemberships]
  );

  return (
    <AccountLayout header={formatMessage({ id: 'memberships' })}>
      <BackArrowLink to={routes.account} />

      {featureMembershipsLoading || membershipStatusLoading ? (
        <LoadingContainer>
          <LoadingAnimation fillColor={Styles.color.primary} />
        </LoadingContainer>
      ) : (
        renderMembershipsList()
      )}
    </AccountLayout>
  );
};

export const Memberships = AccountMemberships;
